/**
 * Sprint 3 — Unified notification centre (side Sheet).
 *
 * Exports:
 *   - useNotificationCount()  — lightweight count query for the topbar bell badge
 *   - default GlobalAuthAlertBanner({ open, onClose })
 */
import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

// ---------------------------------------------------------------------------
// useNotificationCount — lightweight hook used by Layout topbar bell
// ---------------------------------------------------------------------------
export function useNotificationCount() {
  const query = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: () => api.get("/notifications/count").then((r) => r.data),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const count = query.data?.count ?? 0;
  const red = query.data?.red ?? 0;
  const amber = query.data?.amber ?? 0;
  return { count, red, amber };
}

// ---------------------------------------------------------------------------
// useAuthAlerts — used by NavList to show an alert-count badge on the nav
// Returns React Query shape: { data: { total, red, amber } }
// ---------------------------------------------------------------------------
export function useAuthAlerts() {
  return useQuery({
    queryKey: ["notifications", "count"],
    queryFn: () => api.get("/notifications/count").then((r) => r.data),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    select: (d) => ({ total: d.count ?? 0, red: d.red ?? 0, amber: d.amber ?? 0 }),
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function relativeTime(isoString) {
  if (!isoString) return "";
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const SECTION_DEFS = [
  {
    key: "auth",
    label: "Auth alerts",
    types: ["auth_expired", "auth_expiring"],
    accent: (n) => (n.severity === "red" ? "rose" : "amber"),
  },
  {
    key: "vaccination",
    label: "Vaccination auth",
    types: ["vaccination_auth_due"],
    accent: (n) => (n.severity === "red" ? "rose" : "amber"),
  },
  {
    key: "overdue",
    label: "Overdue tasks",
    types: ["task_overdue"],
    accent: () => "rose",
  },
  {
    key: "due_soon",
    label: "Due soon",
    types: ["task_due_soon"],
    accent: () => "amber",
  },
  {
    key: "assigned",
    label: "Recently assigned",
    types: ["task_assigned"],
    accent: () => "brand",
  },
];

const ACCENT_CLASSES = {
  rose: {
    border: "border-l-rose-500",
    bg: "bg-rose-50 dark:bg-rose-900/10",
  },
  amber: {
    border: "border-l-amber-400",
    bg: "bg-amber-50 dark:bg-amber-900/10",
  },
  brand: {
    border: "border-l-brand",
    bg: "bg-brand-50/60 dark:bg-brand-900/10",
  },
};

// ---------------------------------------------------------------------------
// Skeleton card
// ---------------------------------------------------------------------------
function SkeletonCard() {
  return (
    <div className="border-l-4 border-l-zinc-200 bg-zinc-50 rounded-r-md p-3 animate-pulse space-y-2">
      <div className="h-3 w-2/3 rounded bg-zinc-200" />
      <div className="h-2.5 w-full rounded bg-zinc-100" />
      <div className="h-2.5 w-1/3 rounded bg-zinc-100" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notification card
// ---------------------------------------------------------------------------
function NotifCard({ notif, onNavigate }) {
  const sectionDef = SECTION_DEFS.find((s) => s.types.includes(notif.type));
  const accentKey = sectionDef ? sectionDef.accent(notif) : "brand";
  const { border, bg } = ACCENT_CLASSES[accentKey] || ACCENT_CLASSES.brand;

  return (
    <div className={`border-l-4 ${border} ${bg} rounded-r-md px-3 py-2.5 space-y-0.5`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium text-sm text-zinc-900 dark:text-zinc-100 leading-snug">
          {notif.title}
        </span>
        <span className="text-[10px] text-zinc-400 whitespace-nowrap shrink-0">
          {relativeTime(notif.created_at)}
        </span>
      </div>
      {notif.message && (
        <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
          {notif.message}
        </p>
      )}
      {notif.patient_id && (
        <button
          type="button"
          onClick={() => onNavigate(`/patients/${notif.patient_id}`)}
          className="text-xs text-brand hover:underline mt-0.5 inline-block"
        >
          View patient →
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section heading
// ---------------------------------------------------------------------------
function SectionHeading({ label }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-zinc-500 dark:text-zinc-400 px-1 pt-3 pb-1">
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function GlobalAuthAlertBanner({ open, onClose }) {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications").then((r) => r.data),
    staleTime: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const items = data?.items ?? [];
  const totalCount = data?.count ?? items.length;
  const red = data?.red ?? items.filter((n) => n.severity === "red").length;
  const amber = data?.amber ?? items.filter((n) => n.severity === "amber").length;

  const badgeBg = red > 0 ? "bg-rose-500" : amber > 0 ? "bg-amber-400" : "bg-zinc-300";

  // Group into sections, skipping empty ones.
  const sections = useMemo(() => {
    return SECTION_DEFS.map((def) => ({
      ...def,
      notifications: items.filter((n) => def.types.includes(n.type)),
    })).filter((s) => s.notifications.length > 0);
  }, [items]);

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-80 sm:w-96 flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="flex-none px-4 pt-4 pb-3 border-b border-zinc-200 dark:border-zinc-800">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Bell className="w-4 h-4 text-zinc-600 dark:text-zinc-300" strokeWidth={2} />
            Notifications
            {totalCount > 0 && (
              <span
                className={`inline-flex items-center justify-center rounded-full ${badgeBg} text-white text-[10px] font-bold px-2 py-0.5 min-w-[22px]`}
              >
                {totalCount}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {/* Scrollable content */}
        <div
          className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5"
          role="region"
          aria-label="Notifications"
        >
          {isLoading ? (
            <div className="space-y-2 pt-2">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : sections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-zinc-400">
              <Bell className="w-9 h-9 mb-3 opacity-30" />
              <p className="text-sm text-zinc-500 font-medium">All clear</p>
              <p className="text-xs text-zinc-400 mt-1">Nothing to action right now</p>
            </div>
          ) : (
            sections.map((section) => (
              <div key={section.key}>
                <SectionHeading label={section.label} />
                <div className="space-y-1.5">
                  {section.notifications.map((notif, idx) => (
                    <NotifCard
                      key={notif.id ?? `${section.key}-${idx}`}
                      notif={notif}
                      onNavigate={handleNavigate}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex-none px-4 py-3 border-t border-zinc-100 dark:border-zinc-800">
          <Link
            to="/profile#notifications"
            onClick={onClose}
            className="text-xs text-zinc-500 hover:text-brand hover:underline transition-colors"
          >
            Notification preferences →
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
